/**
 * TradePanel
 * 交易面板组件（适配OKX交易终端样式）
 * 参照 OKX 交易面板重构
 *
 * @author page
 * @date 2026-04-23
 */
import React, {useEffect, useState} from 'react';
import {App, Form, InputNumber, Slider} from 'antd';
import {simulateTradingService} from '../../services/simulateService';
import type {SimulatePosition} from '../../types/simulate';
import styles from './TradePanel.module.css';

const uid = () => Math.random().toString(36).slice(-8);

interface TradePanelProps {
    tradingPair: string;
    availableBalance: string;
    tradingMode: 'spot' | 'futures';
    positions: SimulatePosition[];
    currentPrice?: number;
    onOrderSuccess?: () => void;
    /** 当前品种已有活跃持仓的杠杆（有值时锁定不可调） */
    activePositionLeverage?: number | null;
}

/** 持仓类型 */
type PositionType = 'open' | 'close';
/** 委托类型 */
type OrderType = 'limit' | 'market' | 'stop-limit';

const TradePanel: React.FC<TradePanelProps> = ({
                                                   tradingPair,
                                                   availableBalance,
                                                   tradingMode,
                                                   positions,
                                                   currentPrice,
                                                   onOrderSuccess,
                                                   activePositionLeverage,
                                               }) => {
    const {message: messageApi} = App.useApp();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    // 持仓类型：open 开仓，close 平仓
    const [positionType, setPositionType] = useState<PositionType>('open');
    // 委托类型：limit 限价，market 市价
    const [orderType, setOrderType] = useState<OrderType>('market');
    // 杠杆倍数（有活跃持仓时锁定）
    const [leverage, setLeverage] = useState(3);
    // 仓位模式：cross 逐仓
    const [marginMode] = useState('cross');
    // 滑块百分比
    const [sliderValue, setSliderValue] = useState(0);
    // 面板内部Tab：交易 / 辅助工具
    const [panelTab, setPanelTab] = useState<'trade' | 'tools'>('trade');

    const baseSymbol = tradingPair.split('/')[0];

    // 价格输入框为空时填充当前价格
    useEffect(() => {
        const currentVal = form.getFieldValue('price');
        if (currentPrice && currentPrice > 0 && (null === currentVal || undefined === currentVal || '' === currentVal)) {
            form.setFieldValue('price', currentPrice);
        }
    }, [tradingPair, currentPrice]);

    // 同步活跃持仓杠杆（有持仓时锁定，无持仓时保持可调）
    useEffect(() => {
        if (null !== activePositionLeverage && undefined !== activePositionLeverage) {
            setLeverage(activePositionLeverage);
        }
    }, [activePositionLeverage]);

    const handleSubmit = async (direction: 0 | 1) => {
        try {
            setLoading(true);
            const priceVal = form.getFieldValue('price');
            const amountVal = form.getFieldValue('amount');
            if (!amountVal || amountVal <= 0) {
                messageApi.warning('请输入数量');
                return;
            }
            // 合约平仓：调用closePosition API
            if ('futures' === tradingMode && 'close' === positionType) {
                // 平多(direction=0): 关闭LONG(side=0); 平空(direction=1): 关闭SHORT(side=1)
                const targetSide = 0 === direction ? 0 : 1;
                const targetPos = positions.find(p => p.symbol === tradingPair && 1 === p.mode && p.side === targetSide);
                if (!targetPos || !targetPos.id) {
                    messageApi.warning('无对应持仓可平');
                    setLoading(false);
                    return;
                }
                if (amountVal > Number(targetPos.quantity)) {
                    messageApi.warning(`平仓数量(${amountVal})超过持仓数量(${targetPos.quantity})`);
                    setLoading(false);
                    return;
                }
                const closeQty = amountVal >= Number(targetPos.quantity) ? undefined : amountVal;
                await simulateTradingService.closePosition(targetPos.id, closeQty);
                messageApi.success('平仓成功');
                form.resetFields(['amount', 'price']);
                setSliderValue(0);
                onOrderSuccess?.();
                return;
            }
            // 现货卖出走平仓逻辑
            if ('spot' === tradingMode && 1 === direction) {
                const currentPos = positions.find(p => p.symbol === tradingPair && 0 === p.mode && 0 === p.side);
                if (!currentPos || !currentPos.id) {
                    messageApi.warning('无现货持仓可卖');
                    setLoading(false);
                    return;
                }
                const holdQty = Number(currentPos.quantity);
                if (amountVal > holdQty) {
                    messageApi.warning(`卖出数量(${amountVal})超过持仓数量(${currentPos.quantity})`);
                    setLoading(false);
                    return;
                }
                const closeQty = amountVal >= holdQty ? undefined : amountVal;
                await simulateTradingService.closePosition(currentPos.id, closeQty);
                messageApi.success('卖出成功');
                form.resetFields(['amount', 'price']);
                setSliderValue(0);
                onOrderSuccess?.();
                return;
            }
            // 现货买入：校验余额
            if ('spot' === tradingMode && 0 === direction) {
                const cost = (priceVal || currentPrice || 0) * amountVal;
                if (cost > Number(availableBalance)) {
                    messageApi.warning('USDT余额不足');
                    setLoading(false);
                    return;
                }
            }
            await simulateTradingService.placeOrder({
                exchange: 'OKX',
                symbol: tradingPair,
                side: direction === 0 ? 0 : 1,
                type: orderType === 'market' ? 0 : 1,
                price: orderType !== 'market' && priceVal ? priceVal.toString() : undefined,
                quantity: amountVal.toString(),
                tradingMode: 'spot' === tradingMode ? 0 : 1,
                leverage: 'futures' === tradingMode ? leverage : undefined,
            });
            messageApi.success('模拟下单成功');
            form.resetFields(['amount', 'price']);
            setSliderValue(0);
            onOrderSuccess?.();
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '模拟下单失败');
        } finally {
            setLoading(false);
        }
    };

    // 基于当前价格计算买卖报价
    const bestBidPrice = currentPrice && currentPrice > 0 ? (currentPrice * 0.9995).toFixed(4) : null;
    const bestAskPrice = currentPrice && currentPrice > 0 ? (currentPrice * 1.0005).toFixed(4) : null;

    const handleSliderChange = (value: number) => {
        setSliderValue(value);
        // 根据百分比计算数量
        if (availableBalance && value > 0) {
            const balance = parseFloat(availableBalance) || 0;
            // 简化计算：假设价格从表单获取或使用默认值
            const price = form.getFieldValue('price') || 1;
            const amount = (balance * value / 100) / price;
            form.setFieldValue('amount', parseFloat(amount.toFixed(4)));
        }
    };

    // 计算当前交易对的多仓和空仓可平量
    const currentPositions = positions.filter(p => p.symbol === tradingPair && 1 === p.mode);
    const longQty = currentPositions.filter(p => 0 === p.side).reduce((sum, p) => sum + Number(p.quantity), 0).toString();
    const shortQty = currentPositions.filter(p => 1 === p.side).reduce((sum, p) => sum + Number(p.quantity), 0).toString();

    return (
        <div className={styles.container} style={{minHeight: 500}}>
            {/* 面板Tab：交易 / 辅助工具 */}
            <div style={{
                display: 'flex', borderBottom: '1px solid var(--pt-border-subtle)',
                backgroundColor: 'var(--pt-bg-panel)', flexShrink: 0
            }}>
                <div
                    style={{
                        padding: '10px 16px', fontSize: 12, cursor: 'pointer',
                        color: 'trade' === panelTab ? 'var(--pt-text-primary)' : 'var(--pt-text-secondary)',
                        borderBottom: 'trade' === panelTab ? '2px solid var(--pt-primary)' : '2px solid transparent',
                        fontWeight: 'trade' === panelTab ? 600 : 400,
                    }}
                    onClick={() => setPanelTab('trade')}
                >
                    交易
                </div>
                <div
                    style={{
                        padding: '10px 16px', fontSize: 12, cursor: 'pointer',
                        color: 'tools' === panelTab ? 'var(--pt-text-primary)' : 'var(--pt-text-secondary)',
                        borderBottom: 'tools' === panelTab ? '2px solid var(--pt-primary)' : '2px solid transparent',
                        fontWeight: 'tools' === panelTab ? 600 : 400,
                    }}
                    onClick={() => setPanelTab('tools')}
                >
                    辅助工具
                </div>
            </div>

            {'trade' === panelTab && (
                <div className={styles.inner}>
                    {/* 合约：开仓/平仓 标签 | 现货：买入/卖出 */}
                    {'futures' === tradingMode && (
                        <div className={styles.postTypeBox}>
                            <div className={styles.tabs}>
                                <div
                                    className={`${styles.tabPane} ${positionType === 'open' ? styles.tabPaneGreenActive : ''} ${positionType === 'open' ? styles.tabPaneGreen : ''}`}
                                    onClick={() => setPositionType('open')}
                                >
                                    开仓
                                </div>
                                <div
                                    className={`${styles.tabPane} ${positionType === 'close' ? styles.tabPaneRedActive : ''} ${positionType === 'close' ? styles.tabPaneRed : ''}`}
                                    onClick={() => setPositionType('close')}
                                >
                                    平仓
                                </div>
                            </div>
                        </div>
                    )}
                    {'spot' === tradingMode && (
                        <div className={styles.postTypeBox}>
                            <div className={styles.tabs}>
                                <div
                                    className={`${styles.tabPane} ${positionType === 'open' ? styles.tabPaneGreenActive : ''} ${positionType === 'open' ? styles.tabPaneGreen : ''}`}
                                    onClick={() => setPositionType('open')}
                                >
                                    买入
                                </div>
                                <div
                                    className={`${styles.tabPane} ${positionType === 'close' ? styles.tabPaneRedActive : ''} ${positionType === 'close' ? styles.tabPaneRed : ''}`}
                                    onClick={() => setPositionType('close')}
                                >
                                    卖出
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{marginBottom: 12}}/>

                    {/* 合约：逐仓/杠杆 设置 */}
                    {'futures' === tradingMode && (
                        <div className={styles.topBox}>
                            <div className={styles.settingBox}>
                                <button className={styles.settingBtn}>
                                    <span>逐仓</span>
                                    <i className="iconfont okx-trading-arrow-sort"/>
                                </button>
                            </div>
                            <div className={styles.settingBox}>
                                {null !== activePositionLeverage && undefined !== activePositionLeverage ? (
                                    // 有持仓：锁定显示，不可调节
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '4px 8px',
                                        color: 'var(--pt-text-secondary)',
                                        fontSize: 12,
                                    }}>
                                        <span style={{color: 'var(--pt-primary)', fontWeight: 500}}>{leverage}x</span>
                                    </span>
                                ) : (
                                    // 无持仓：可调节杠杆
                                    <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                        <button
                                            className={styles.settingBtn}
                                            onClick={() => setLeverage(Math.max(1, leverage - 1))}
                                            style={{padding: '2px 6px', fontSize: 14, lineHeight: 1}}
                                        >−
                                        </button>
                                        <span style={{
                                            color: 'var(--pt-primary)',
                                            fontWeight: 500,
                                            minWidth: 24,
                                            textAlign: 'center',
                                            fontSize: 12
                                        }}>{leverage}x</span>
                                        <button
                                            className={styles.settingBtn}
                                            onClick={() => setLeverage(Math.min(125, leverage + 1))}
                                            style={{padding: '2px 6px', fontSize: 14, lineHeight: 1}}
                                        >+
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 委托类型 */}
                    <div className={styles.orderTypeTabs}>
                        <div
                            className={`${styles.orderTab} ${orderType === 'market' ? styles.orderTabActive : ''}`}
                            onClick={() => setOrderType('market')}
                        >
                            市价委托
                        </div>
                        <div
                            className={`${styles.orderTab} ${orderType === 'limit' ? styles.orderTabActive : ''}`}
                            onClick={() => setOrderType('limit')}
                        >
                            限价委托
                        </div>
                        <div
                            className={`${styles.orderTab} ${orderType === 'stop-limit' ? styles.orderTabActive : ''}`}
                            onClick={() => setOrderType('stop-limit')}
                        >
                            <span>止盈止损</span>
                            <i className="iconfont okds-chevron-down"/>
                        </div>
                    </div>

                    {/* 表单 */}
                    <Form form={form} layout="vertical" className={styles.form}>
                        {/* 价格输入 */}
                        {orderType !== 'market' && (
                            <div className={styles.inputLine}>
                                <div className={styles.inputItem}>
                                    <div className={styles.inputLabel}>价格 (USDT)</div>
                                    <div className={styles.inputControl}>
                                        <Form.Item name="price" noStyle>
                                            <InputNumber
                                                className={styles.inputNumber}
                                                min={0}
                                                step={0.00001}
                                                placeholder="0.00"
                                            />
                                        </Form.Item>
                                        <div className={styles.inputSuffix}>
                                            <button type="button" className={styles.suffixBtn}>
                                                <i className="iconfont okds-arrow-chevron-down-sm"/>
                                            </button>
                                            <div className={styles.suffixLine}/>
                                            <button type="button" className={styles.suffixBtn}>
                                                <i className="iconfont okds-arrow-chevron-down-sm"
                                                   style={{transform: 'rotate(180deg)'}}/>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 数量输入 */}
                        <div className={styles.inputLine}>
                            <div className={styles.inputItem}>
                                <div className={styles.inputLabel}>数量 ({baseSymbol})</div>
                                <div className={styles.inputControl}>
                                    <Form.Item name="amount" noStyle>
                                        <InputNumber
                                            className={styles.inputNumber}
                                            min={0}
                                            step={1}
                                            placeholder="0.00"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                        {/* 滑块 */}
                        <div className={styles.sliderContainer}>
                            <Slider
                                value={sliderValue}
                                onChange={handleSliderChange}
                                marks={{
                                    0: '0%',
                                    25: '25%',
                                    50: '50%',
                                    75: '75%',
                                    100: '100%',
                                }}
                                className={styles.slider}
                            />
                        </div>

                        {/* 合约：可平量信息 */}
                        {'futures' === tradingMode && (
                            <div className={styles.availableRow}>
                                <div className={styles.availableItem}>
                                    <p>
                                        <span className={styles.label}>空仓持仓</span>
                                        <span
                                            className={`${styles.value} ${styles.colorDown}`}>{shortQty} {baseSymbol}</span>
                                    </p>
                                    <div>
                                        <span className={styles.label}>可平量</span>
                                        <span
                                            className={`${styles.value} ${styles.colorDown}`}>{shortQty} {baseSymbol}</span>
                                    </div>
                                </div>
                                <div className={styles.availableItem}>
                                    <p>
                                        <span className={styles.label}>多仓持仓</span>
                                        <span
                                            className={`${styles.value} ${styles.colorUp}`}>{longQty} {baseSymbol}</span>
                                    </p>
                                    <div>
                                        <span className={styles.label}>可平量</span>
                                        <span
                                            className={`${styles.value} ${styles.colorUp}`}>{longQty} {baseSymbol}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 合约：两个按钮 */}
                        {'futures' === tradingMode && (
                            <div className={styles.submitBtnContainer}>
                                <div className={styles.buttonWrapper}>
                                    <button
                                        type="button"
                                        className={`${styles.submitBtn} ${styles.positiveBtn} ${positionType === 'close' ? styles.simulateBtn : ''}`}
                                        onClick={() => handleSubmit(0)}
                                        disabled={loading}
                                    >
                                        <span>{positionType === 'open' ? '开多' : '平多'}</span>
                                        <span className={styles.demoBtn}>模拟</span>
                                    </button>
                                </div>
                                <div className={styles.buttonWrapper}>
                                    <button
                                        type="button"
                                        className={`${styles.submitBtn} ${styles.negativeBtn} ${positionType === 'close' ? styles.simulateBtn : ''}`}
                                        onClick={() => handleSubmit(1)}
                                        disabled={loading}
                                    >
                                        <span>{positionType === 'open' ? '开空' : '平空'}</span>
                                        <span className={styles.demoBtn}>模拟</span>
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* 现货：单个按钮（由买入/卖出Tab决定） */}
                        {'spot' === tradingMode && (
                            <div className={styles.submitBtnContainer}>
                                <div className={styles.buttonWrapper}>
                                    <button
                                        type="button"
                                        className={`${styles.submitBtn} ${'open' === positionType ? styles.positiveBtn : styles.negativeBtn}`}
                                        onClick={() => handleSubmit('open' === positionType ? 0 : 1)}
                                        disabled={loading}
                                    >
                                        <span>{'open' === positionType ? `买入 ${baseSymbol}` : `卖出 ${baseSymbol}`}</span>
                                        <span className={styles.demoBtn}>模拟</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* 价格范围 */}
                        <div className={styles.priceRangeBox}>
                            <div>
                                <span className={styles.priceRangeName}>最高买价&nbsp;</span>
                                <span
                                    className={styles.priceRangeValue}>{bestBidPrice ? `₮${bestBidPrice}` : '--'}</span>
                            </div>
                            <div>
                                <span className={styles.priceRangeName}>最低卖价&nbsp;</span>
                                <span
                                    className={styles.priceRangeValue}>{bestAskPrice ? `₮${bestAskPrice}` : '--'}</span>
                            </div>
                        </div>
                    </Form>
                </div>
            )}

            {'tools' === panelTab && (
                <div className={styles.inner}>
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--pt-text-secondary)',
                        fontSize: 13
                    }}>
                        辅助工具
                    </div>
                </div>
            )}
        </div>
    );
};

export default TradePanel;
